/**
 * useBilling Hook
 * Plan limits, feature gates, and subscription state
 */

import { useQuery } from '@tanstack/react-query';
import { useOrganization } from '../providers/OrganizationProvider';
import { fetchWithAuth } from '../lib/api';

export const PLAN_LIMITS = {
  free: 5,
  pro: 10,
  business: null,
};

function resolveMaxActiveEmployees(subscription, effectivePlan) {
  if (subscription && 'maxActiveEmployees' in subscription) {
    return subscription.maxActiveEmployees;
  }
  if (effectivePlan === 'business') return null;
  return PLAN_LIMITS[effectivePlan] ?? PLAN_LIMITS.free;
}

export function useBilling() {
  const { organization, organizationId, refetchOrganizations } = useOrganization();

  const { data: subscription, isLoading, refetch } = useQuery({
    queryKey: ['subscription', organizationId],
    queryFn: async () => {
      const res = await fetchWithAuth('/api/billing/subscription');
      if (!res.ok) throw new Error('Failed to fetch subscription');
      return res.json();
    },
    enabled: !!organizationId,
    staleTime: 2 * 60 * 1000,
  });

  const effectivePlan = subscription?.effectivePlan || organization?.plan || 'free';
  const isTrialing = subscription?.isTrialing || false;
  const isPaid = subscription?.isPaid || effectivePlan === 'pro' || effectivePlan === 'business';
  const isFree = effectivePlan === 'free' && !isTrialing;
  const isPro = effectivePlan === 'pro';
  const isBusiness = effectivePlan === 'business';

  const maxActiveEmployees = resolveMaxActiveEmployees(subscription, effectivePlan);
  const activeEmployeeCount = subscription?.activeEmployeeCount ?? 0;
  const needsEmployeeReduction = subscription?.needsEmployeeReduction || false;
  const canStartTrial = subscription?.canStartTrial ?? false;
  const trialDaysRemaining = subscription?.trialDaysRemaining;

  const features = subscription?.features || {
    myDataSync: isPaid,
    exportReports: isPaid,
    aiAssistant: isPaid,
    prioritySupport: isPaid,
  };

  const hasFeature = (featureName) => !!features[featureName];

  const canAddEmployee = (currentCount = activeEmployeeCount) => {
    if (maxActiveEmployees === null) return true;
    return currentCount < maxActiveEmployees;
  };

  const getEmployeeLimitMessage = (currentCount = activeEmployeeCount) => {
    if (canAddEmployee(currentCount)) return null;
    return {
      title: 'Όριο ενεργών υπαλλήλων',
      message: `Έχετε φτάσει το όριο των ${maxActiveEmployees} ενεργών υπαλλήλων για το ${effectivePlan === 'business' ? 'Business' : effectivePlan === 'pro' ? 'Pro' : 'Free'} plan.`,
      canStartTrial,
    };
  };

  const getUpgradeMessage = (featureName) => {
    const messages = {
      myDataSync: 'Σύνδεση με myDATA',
      exportReports: 'Export PDF/Excel',
      aiAssistant: 'AI Assistant',
      prioritySupport: 'Priority Support',
      unlimitedEmployees: 'Περισσότεροι ενεργοί υπάλληλοι',
    };
    return messages[featureName] || 'Pro/Business feature';
  };

  return {
    subscription,
    isLoading,
    refetch,
    refetchOrganizations,

    effectivePlan,
    plan: subscription?.plan || organization?.plan || 'free',
    planStatus: subscription?.planStatus || 'active',
    isFree,
    isPaid,
    isPro,
    isBusiness,
    isTrialing,
    trialDaysRemaining,
    canStartTrial,
    demoMode: subscription?.demoMode,

    maxActiveEmployees,
    activeEmployeeCount,
    needsEmployeeReduction,
    canAddEmployee,
    getEmployeeLimitMessage,

    features,
    hasFeature,
    getUpgradeMessage,
  };
}
